/**
 * Every file the assistant returns opens in Files (Khalid, 2026-10-13): kinds the UI can draw
 * preview in their tab; anything else still gets a tab, with what the file is and a Download
 * button. Downloads come from the reply's own content (a document's text) or from a URL inside the
 * AVA API. No other host is fetched, because the request carries the user's token.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */

export const PREVIEW = Object.freeze({
  MARKDOWN: 'markdown',
  PDF: 'pdf',
  CHART: 'chart',
  DASHBOARD: 'dashboard',
  TEXT: 'text',
  DOWNLOAD: 'download',
});

const FALLBACK_NAME = 'AVA file';
const UNSAFE_NAME_CHARS = /[\\/:*?"<>|]/g;
const DOC_EXTENSION = /\.(md|pdf|txt|csv)$/i;
const EXTENSION = /\.([a-z0-9]{1,8})$/i;
const TEXT_KINDS = Object.freeze({ text: { ext: '.txt', type: 'text/plain;charset=utf-8' }, csv: { ext: '.csv', type: 'text/csv;charset=utf-8' } });
const MARKDOWN_TYPE = 'text/markdown;charset=utf-8';
const KB = 1024;

const hasText = (v) => typeof v === 'string';

/** Which preview a file gets in its tab. */
export function previewOf(artifact) {
  const kind = artifact && artifact.kind;
  const data = (artifact && artifact.data) || {};
  if (kind === 'markdown' && hasText(data.markdown)) return PREVIEW.MARKDOWN;
  if (kind === 'pdf' && hasText(data.markdown)) return PREVIEW.PDF;
  if (kind === 'chart') return PREVIEW.CHART;
  if (kind === 'dashboard') return PREVIEW.DASHBOARD;
  if (TEXT_KINDS[kind] && hasText(data.text)) return PREVIEW.TEXT;
  return PREVIEW.DOWNLOAD;
}

const safeName = (name) => String(name || '').replace(UNSAFE_NAME_CHARS, '-').trim() || FALLBACK_NAME;
const withExtension = (name, ext) => `${safeName(name).replace(DOC_EXTENSION, '')}${ext}`;

/** True when `url` is inside the AVA API (same origin, under its path). */
function insideApi(url, apiBase) {
  try {
    const target = new URL(url);
    const base = new URL(apiBase);
    return target.origin === base.origin && target.pathname.startsWith(base.pathname);
  } catch (err) {
    return false;
  }
}

/**
 * Where a file downloads from, or null when there is nothing to download yet.
 * @returns {{ url: string, filename: string } | { content: string, type: string, filename: string } | null}
 */
export function downloadOf(artifact, { apiBase }) {
  if (!artifact) return null;
  if (artifact.url) return insideApi(artifact.url, apiBase) ? { url: artifact.url, filename: safeName(artifact.name) } : null;
  const data = artifact.data || {};
  if ((artifact.kind === 'markdown' || artifact.kind === 'pdf') && hasText(data.markdown)) {
    return { content: data.markdown, type: MARKDOWN_TYPE, filename: withExtension(artifact.name, '.md') };
  }
  const text = TEXT_KINDS[artifact.kind];
  if (text && hasText(data.text)) return { content: data.text, type: text.type, filename: withExtension(artifact.name, text.ext) };
  return null;
}

/** 412 KB, 2.4 MB: a file size for people (also the attachment chips, attachmentMessages.js). */
export function sizeLabel(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes < KB) return `${bytes} B`;
  const [value, unit] = bytes < KB * KB ? [bytes / KB, 'KB'] : [bytes / (KB * KB), 'MB'];
  return `${Number(value.toFixed(1))} ${unit}`;
}

/** What the download card says about a file: its type (from the extension or kind) and size. */
export function fileDetails(artifact) {
  const name = (artifact && artifact.name) || '';
  const match = name.match(EXTENSION);
  const ext = match ? match[1] : artifact && artifact.kind;
  return { type: ext ? `${String(ext).toUpperCase()} file` : 'File', size: sizeLabel(artifact && artifact.size) };
}
